import { markSessionExpired } from '@/stores/session-store';

interface FetchEndpointOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: any;
  signal?: AbortSignal;
}

export class ApiError extends Error {
  details: string | null;
  status: number;
  requestId: string | null;
  transient: boolean;
  errorCode: string | null;

  constructor(
    message: string,
    status: number,
    details?: string | null,
    requestId?: string | null,
    transient?: boolean,
    errorCode?: string | null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details ?? null;
    this.requestId = requestId ?? null;
    this.transient = transient ?? false;
    this.errorCode = errorCode ?? null;
  }
}

export async function fetchEndpointData<T>(url: string | URL, opts: FetchEndpointOpts = {}): Promise<T> {
  const response = await fetch(url, {
    method: opts.method ?? 'GET',
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    signal: opts.signal,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let json: any;
  try {
    json = await response.json();
  } catch {
    // Non-JSON response (e.g. plain-text 429 from rate limiter, 502 from reverse proxy)
    if (!response.ok) {
      // A bodyless 401 comes from an edge proxy rather than our API, so there's no
      // error_code to inspect. Treating it as a dead session is the right default.
      if (response.status === 401) markSessionExpired();
      throw new ApiError(
        response.statusText || `Request failed (${response.status})`,
        response.status,
        null,
        null,
        response.status === 429 || response.status >= 500,
      );
    }
    throw new ApiError('Invalid response from server', response.status);
  }
  if (!response.ok) {
    const errorMsg =
      typeof json.error === 'string'
        ? json.error
        : typeof json.error === 'object' && json.error?.message
          ? json.error.message
          : (json.err ?? `Request failed: ${response.statusText}`);

    // Match on the code, not the status. 401 is ambiguous here: AppError::AuthError
    // (an upstream provider rejecting *us*, e.g. a bad Daytona key) also surfaces as
    // 401, and signing every user out over that would be worse than the bug this fixes.
    if (response.status === 401 && json.error_code === 'UNAUTHENTICATED') {
      markSessionExpired();
    }

    throw new ApiError(errorMsg, response.status, json.details, json.request_id, json.transient, json.error_code);
  }
  return json as T;
}
